<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
    <form @submit.prevent="handleSubmit">
      <!-- 标题输入 -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          公告标题 <span class="text-red-500">*</span>
        </label>
        <input v-model="formData.title" type="text" placeholder="请输入公告标题"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          :class="{ 'border-red-500': errors.title }" />
        <p v-if="errors.title" class="mt-1 text-sm text-red-500">{{ errors.title }}</p>
      </div>

      <!-- 优先级选择 -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          优先级 <span class="text-red-500">*</span>
        </label>
        <select v-model="formData.priority"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
          <option :value="MessagePriority.NORMAL">普通</option>
          <option :value="MessagePriority.HIGH">高优先级</option>
          <option :value="MessagePriority.LOW">低优先级</option>
        </select>
      </div>

      <!-- 富文本编辑器 -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          公告内容 <span class="text-red-500">*</span>
        </label>
        <QuillEditor v-model:content="formData.content" content-type="html" theme="snow" :toolbar="toolbarOptions"
          class="bg-white dark:bg-gray-700" :class="{ 'border-red-500': errors.content }" />
        <p v-if="errors.content" class="mt-1 text-sm text-red-500">{{ errors.content }}</p>
      </div>

      <!-- 接收者选择 (仅创建模式) -->
      <div v-if="mode === 'create'" class="mb-6">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          接收者 (留空则发送给所有用户)
        </label>
        <input v-model="recipientInput" type="text" placeholder="输入用户ID,多个用户用逗号分隔,例如: 1,2,3"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          提示: 留空表示发送给所有用户
        </p>
      </div>

      <!-- 操作按钮 -->
      <div class="flex justify-end gap-3">
        <button type="button" @click="handleCancel"
          class="cancel-btn px-6">
          取消
        </button>
        <button type="submit" :disabled="isSubmitting"
          class="primary-btn px-6 disabled:opacity-50 disabled:cursor-not-allowed">
          {{ isSubmitting ? '发送中...' : '发送公告' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { MessagePriority, MessageType } from '@/types/message'
import type { CreateMessageRequest, UpdateMessageRequest } from '@/types/message'

/**
 * 组件属性
 */
interface Props {
  /** 表单模式: create=创建, edit=编辑 */
  mode?: 'create' | 'edit'
  /** 初始数据 */
  initialData?: Partial<CreateMessageRequest>
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'create',
  initialData: () => ({}),
})

/**
 * 组件事件
 */
interface Emits {
  (e: 'submit', data: CreateMessageRequest | UpdateMessageRequest): void
  (e: 'cancel'): void
}

const emit = defineEmits<Emits>()

/**
 * 表单数据
 */
const formData = reactive<CreateMessageRequest>({
  type: props.initialData.type || MessageType.SYSTEM_NOTIFICATION,
  title: props.initialData.title || '',
  content: props.initialData.content || '',
  priority: props.initialData.priority || MessagePriority.NORMAL,
  userIds: props.initialData.userIds || [],
})

/**
 * 接收者输入 (逗号分隔的用户ID)
 */
const recipientInput = ref<string>(
  props.initialData.userIds?.join(',') || ''
)

/**
 * 表单验证错误
 */
const errors = reactive({
  title: '',
  content: '',
})

/**
 * 提交状态
 */
const isSubmitting = ref(false)

/**
 * Quill编辑器工具栏配置
 */
const toolbarOptions = [
  ['bold', 'italic', 'underline', 'strike'],
  ['blockquote', 'code-block'],
  [{ header: 1 }, { header: 2 }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ color: [] }, { background: [] }],
  ['clean'],
]

/**
 * 表单验证
 */
const validateForm = (): boolean => {
  errors.title = ''
  errors.content = ''

  if (!formData.title.trim()) {
    errors.title = '请输入消息标题'
    return false
  }

  if (formData.title.length > 200) {
    errors.title = '标题长度不能超过200个字符'
    return false
  }

  if (!formData.content.trim()) {
    errors.content = '请输入消息内容'
    return false
  }

  return true
}

/**
 * 处理表单提交
 */
const handleSubmit = () => {
  if (!validateForm()) {
    return
  }

  isSubmitting.value = true

  if (props.mode === 'create') {
    // 创建模式: 解析接收者ID
    if (recipientInput.value.trim()) {
      const ids = recipientInput.value
        .split(',')
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id))
      formData.userIds = ids.length > 0 ? ids : []
    } else {
      // 留空表示发送给所有用户,后端会处理
      formData.userIds = []
    }
    emit('submit', { ...formData } as CreateMessageRequest)
  } else {
    // 编辑模式: 只提交必要字段
    const updateData: UpdateMessageRequest = {
      title: formData.title,
      content: formData.content,
      priority: formData.priority,
    }
    emit('submit', updateData)
  }
}

/**
 * 处理取消
 */
const handleCancel = () => {
  emit('cancel')
}
</script>
